import React, { useEffect, useState } from 'react';

const ERROR_BORDER = '#dc3545';
const ERROR_BACKGROUND = '#fff5f5';

const ConditionRow = ({
  condition,
  index,
  metadata,
  conditions,
  onChange,
  onRemove,
  onVariableSelected,
  showValidation = false,
  isValid = true,
  validationErrors = { field: false, operator: false, value: false, subFields: {}, missingValorCriterion: false }
}) => {
  const selectedField = metadata.fields.find(f => f.id === condition.field);
  const operators = selectedField ? selectedField.operators : [];
  const isBetween = condition.operator === 'between';
  const isCompoundBoolean = selectedField && selectedField.type === 'compound_boolean';
  const hasCompoundTrueValue = Array.isArray(condition.value)
    ? condition.value.includes(true)
    : condition.value === true;
  const showSubFields = isCompoundBoolean && hasCompoundTrueValue;
  const isRendaField = condition.field === 'renda_informada' || condition.field === 'renda_presumida';
  const isFaixaValorLocacao = condition.field === 'faixa_valor_locacao';
  const isOrigemRenda = condition.field === 'origem_renda';
  const showOrigemRendaSoma = isOrigemRenda && condition.operator === 'in';

  const [rendaInput, setRendaInput] = useState({ single: '', min: '', max: '' });
  const [negativeValorInput, setNegativeValorInput] = useState('');
  const [faixaValorLocacaoInput, setFaixaValorLocacaoInput] = useState({ min: '', max: '' });
  const [origemRendaSomaInput, setOrigemRendaSomaInput] = useState({ single: '', min: '', max: '' });

  useEffect(() => {
    if (!isRendaField) {
      setRendaInput({ single: '', min: '', max: '' });
      return;
    }

    if (isBetween) {
      setRendaInput({
        single: '',
        min:
          condition.value && condition.value.min !== null && condition.value.min !== undefined
            ? String(condition.value.min).replace('.', ',')
            : '',
        max:
          condition.value && condition.value.max !== null && condition.value.max !== undefined
            ? String(condition.value.max).replace('.', ',')
            : ''
      });
      return;
    }

    setRendaInput({
      min: '',
      max: '',
      single:
        condition.value !== null && condition.value !== undefined
          ? String(condition.value).replace('.', ',')
          : ''
    });
  }, [isRendaField, isBetween, condition.value]);

  // Sincronizar valor de moeda em negativacao
  useEffect(() => {
    if (selectedField && selectedField.id === 'negativacao' && condition.sub_conditions && condition.sub_conditions.valor) {
      const numValue = condition.sub_conditions.valor.value;
      if (numValue !== null && numValue !== undefined && numValue !== '') {
        // Formatar como moeda: 1000000 -> 1.000.000,00
        const formatted = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(numValue).replace('R$', '').trim();
        setNegativeValorInput(formatted);
      } else {
        setNegativeValorInput('');
      }
    }
  }, [selectedField, condition.sub_conditions]);

  // Obter lista de campos já selecionados em outros critérios
  const selectedFieldIds = conditions
    .map((c, i) => i !== index && c.field ? c.field : null)
    .filter(Boolean);

  // Filtrar campos disponíveis (removendo os já selecionados)
  const availableFields = metadata.fields.filter(f => !selectedFieldIds.includes(f.id));

  const handleFieldChange = (e) => {
    const fieldId = e.target.value;
    const field = metadata.fields.find(f => f.id === fieldId);

    if (field && onVariableSelected) {
      onVariableSelected(field.label);
    }
    
    onChange(index, {
      field: fieldId,
      operator: field && field.operators && field.operators[0] ? field.operators[0].value : '',
      value: field ? (
        field.id === 'faixa_valor_locacao'
          ? { min: '', max: '' }
          : field.type === 'multi_select'
            ? []
            : field.type === 'compound_boolean'
              ? [false]
              : ''
      ) : '',
      sub_conditions: undefined
    });
  };

  const handleOperatorChange = (e) => {
    const newOperator = e.target.value;
    const updates = { ...condition, operator: newOperator };

    if (condition.field === 'origem_renda' && newOperator !== 'in' && condition.sub_conditions?.soma_renda) {
      const { soma_renda, ...rest } = condition.sub_conditions;
      updates.sub_conditions = Object.keys(rest).length ? rest : undefined;
    }

    onChange(index, updates);
  };

  const handleValueChange = (value) => {
    onChange(index, { ...condition, value });
  };

  // Converter valor formatado de moeda para número
  const parseMoneyValue = (displayValue) => {
    if (!displayValue) return '';
    // Remove caracteres que não são dígitos
    const numericOnly = displayValue.replace(/\D/g, '');
    return numericOnly ? parseInt(numericOnly, 10) : '';
  };

  // Formatar valor para exibição com máscara de moeda
  const formatMoneyDisplay = (input) => {
    if (!input) return '';
    // Remove tudo que não é número
    let onlyNumbers = input.replace(/\D/g, '');
    if (!onlyNumbers) return '';
    
    // Limita a 1.000.000 (máximo 7 dígitos: 1000000)
    if (parseInt(onlyNumbers, 10) > 1000000) {
      onlyNumbers = '1000000';
    }
    
    // Formata com separador de milhares
    return onlyNumbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  useEffect(() => {
    if (!isFaixaValorLocacao) {
      setFaixaValorLocacaoInput({ min: '', max: '' });
      return;
    }

    const minValue = condition.value && condition.value.min !== null && condition.value.min !== undefined
      ? condition.value.min
      : '';
    const maxValue = condition.value && condition.value.max !== null && condition.value.max !== undefined
      ? condition.value.max
      : '';

    setFaixaValorLocacaoInput({
      min: minValue !== '' ? formatMoneyDisplay(String(minValue)) : '',
      max: maxValue !== '' ? formatMoneyDisplay(String(maxValue)) : ''
    });
  }, [isFaixaValorLocacao, condition.value]);

  useEffect(() => {
    if (!showOrigemRendaSoma) {
      setOrigemRendaSomaInput({ single: '', min: '', max: '' });
      return;
    }

    const somaRenda = condition.sub_conditions && condition.sub_conditions.soma_renda;
    const isSomaBetween = somaRenda && somaRenda.operator === 'between';
    const formatMultiplier = (value) =>
      value !== '' && value !== null && value !== undefined
        ? String(value).replace('.', ',')
        : '';

    if (isSomaBetween) {
      const minValue = somaRenda.value && somaRenda.value.min !== null && somaRenda.value.min !== undefined
        ? somaRenda.value.min
        : '';
      const maxValue = somaRenda.value && somaRenda.value.max !== null && somaRenda.value.max !== undefined
        ? somaRenda.value.max
        : '';

      setOrigemRendaSomaInput({
        single: '',
        min: formatMultiplier(minValue),
        max: formatMultiplier(maxValue)
      });
      return;
    }

    const singleValue = somaRenda && somaRenda.value !== null && somaRenda.value !== undefined
      ? somaRenda.value
      : '';

    setOrigemRendaSomaInput({
      min: '',
      max: '',
      single: formatMultiplier(singleValue)
    });
  }, [showOrigemRendaSoma, condition.sub_conditions]);

  const handleMultiplierInputChange = (displayKey, rawValue, onParsed) => {
    const normalizedDisplayValue = rawValue.replace('.', ',');
    if (!/^\d*,?\d*$/.test(normalizedDisplayValue)) return;

    setOrigemRendaSomaInput((prev) => ({ ...prev, [displayKey]: normalizedDisplayValue }));

    if (
      normalizedDisplayValue === '' ||
      normalizedDisplayValue === ',' ||
      normalizedDisplayValue.endsWith(',')
    ) {
      if (normalizedDisplayValue === '') {
        onParsed('');
      }
      return;
    }

    const numValue = parseFloat(normalizedDisplayValue.replace(',', '.'));
    if (!isNaN(numValue)) {
      onParsed(numValue);
    }
  };

  const handleSubConditionChange = (subFieldId, subOperator, subValue, patch = {}) => {
    const current = (condition.sub_conditions && condition.sub_conditions[subFieldId]) || {};
    onChange(index, {
      ...condition,
      sub_conditions: {
        ...condition.sub_conditions,
        [subFieldId]: {
          ...current,
          operator: subOperator,
          value: subValue,
          ...patch
        }
      }
    });
  };

  const isNegativacaoOptionalField = (subFieldId) =>
    selectedField && selectedField.id === 'negativacao' &&
    (subFieldId === 'valor' || subFieldId === 'valor_percentual');

  const isOptionalSubFieldEnabled = (subFieldId, subCondition) => {
    if (!isNegativacaoOptionalField(subFieldId)) return true;
    return subCondition.enabled === true;
  };

  const handleSubFieldEnabledChange = (subFieldId, enabled) => {
    const subField = selectedField.sub_fields.find(f => f.id === subFieldId);
    const defaultOperator = subField && subField.operators && subField.operators[0]
      ? subField.operators[0].value
      : 'less_equal';
    const current = (condition.sub_conditions && condition.sub_conditions[subFieldId]) || {
      operator: defaultOperator,
      value: ''
    };
    handleSubConditionChange(
      subFieldId,
      current.operator || defaultOperator,
      current.value !== undefined && current.value !== null ? current.value : '',
      { enabled }
    );
  };

  const renderValueInput = () => {
    if (!selectedField) return null;

    const { type, options, step } = selectedField;

    if (isBetween) {
      const isScoreFc = condition.field === 'score_fc';

      if (isFaixaValorLocacao) {
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}>DE:</span>
              <input
                type="text"
                value={faixaValorLocacaoInput.min}
                onChange={(e) => {
                  const formatted = formatMoneyDisplay(e.target.value);
                  setFaixaValorLocacaoInput(prev => ({ ...prev, min: formatted }));
                  const numValue = parseMoneyValue(formatted);
                  handleValueChange({
                    ...(condition.value || {}),
                    min: numValue === '' ? '' : numValue
                  });
                }}
                placeholder="0"
                style={{ width: '130px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <span style={{ fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}>À:</span>
              <input
                type="text"
                value={faixaValorLocacaoInput.max}
                onChange={(e) => {
                  const formatted = formatMoneyDisplay(e.target.value);
                  setFaixaValorLocacaoInput(prev => ({ ...prev, max: formatted }));
                  const numValue = parseMoneyValue(formatted);
                  handleValueChange({
                    ...(condition.value || {}),
                    max: numValue === '' ? '' : numValue
                  });
                }}
                placeholder="1.000.000"
                style={{ width: '130px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            <span style={{ fontSize: '12px', color: '#0066cc', fontWeight: '500', whiteSpace: 'nowrap' }}>
              Vinculado ao total Aluguel + Condomínio + IPTU.
            </span>
          </div>
        );
      }

      return (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type={isRendaField ? 'text' : 'number'}
            inputMode={isRendaField ? 'decimal' : 'numeric'}
            step={isRendaField ? undefined : (step || '1')}
            value={isRendaField
              ? rendaInput.min
              : (condition.value && (condition.value.min !== null && condition.value.min !== undefined) ? condition.value.min : '')}
            onChange={(e) => {
              const value = e.target.value;

              if (isRendaField) {
                const normalizedDisplayValue = value.replace('.', ',');
                if (!/^\d*,?\d*$/.test(normalizedDisplayValue)) return;
                setRendaInput(prev => ({ ...prev, min: normalizedDisplayValue }));

                if (normalizedDisplayValue === '' || normalizedDisplayValue === ',' || normalizedDisplayValue.endsWith(',')) {
                  if (normalizedDisplayValue === '') {
                    handleValueChange({ ...condition.value, min: '' });
                  }
                  return;
                }

                const numValue = parseFloat(normalizedDisplayValue.replace(',', '.'));
                if (!isNaN(numValue)) {
                  handleValueChange({ ...condition.value, min: numValue });
                }
                return;
              }

              if (value === '') {
                handleValueChange({ ...condition.value, min: '' });
                return;
              }

              const numValue = parseFloat(value);
              if (isScoreFc && numValue > 1000) {
                return;
              }
              if (!isNaN(numValue)) {
                handleValueChange({ ...condition.value, min: numValue });
              }
            }}
            placeholder="Mín"
            style={{ width: '100px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
          <span>e</span>
          <input
            type={isRendaField ? 'text' : 'number'}
            inputMode={isRendaField ? 'decimal' : 'numeric'}
            step={isRendaField ? undefined : (step || '1')}
            value={isRendaField
              ? rendaInput.max
              : (condition.value && (condition.value.max !== null && condition.value.max !== undefined) ? condition.value.max : '')}
            onChange={(e) => {
              const value = e.target.value;

              if (isRendaField) {
                const normalizedDisplayValue = value.replace('.', ',');
                if (!/^\d*,?\d*$/.test(normalizedDisplayValue)) return;
                setRendaInput(prev => ({ ...prev, max: normalizedDisplayValue }));

                if (normalizedDisplayValue === '' || normalizedDisplayValue === ',' || normalizedDisplayValue.endsWith(',')) {
                  if (normalizedDisplayValue === '') {
                    handleValueChange({ ...condition.value, max: '' });
                  }
                  return;
                }

                const numValue = parseFloat(normalizedDisplayValue.replace(',', '.'));
                if (!isNaN(numValue)) {
                  handleValueChange({ ...condition.value, max: numValue });
                }
                return;
              }

              if (value === '') {
                handleValueChange({ ...condition.value, max: '' });
                return;
              }

              const numValue = parseFloat(value);
              if (isScoreFc && numValue > 1000) {
                return;
              }
              if (!isNaN(numValue)) {
                handleValueChange({ ...condition.value, max: numValue });
              }
            }}
            placeholder="Máx"
            style={{ width: '100px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
          {isScoreFc && (
            <span style={{ fontSize: '12px', color: '#0066cc', fontWeight: '500', whiteSpace: 'nowrap' }}>
              Máximo: 1000
            </span>
          )}
          {isRendaField && (
            <span style={{ fontSize: '12px', color: '#0066cc', fontWeight: '500', whiteSpace: 'nowrap' }}>
              x Aluguel + Encargos
            </span>
          )}
        </div>
      );
    }

    switch (type) {
      case 'numeric':
      case 'currency':
        const isScoreFc = condition.field === 'score_fc';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type={isRendaField ? 'text' : 'number'}
              step={isRendaField ? undefined : (step || '1')}
              value={isRendaField
                ? rendaInput.single
                : (condition.value !== null && condition.value !== undefined ? condition.value : '')}
              onChange={(e) => {
                const value = e.target.value;

                if (isRendaField) {
                  const normalizedDisplayValue = value.replace('.', ',');
                  if (!/^\d*,?\d*$/.test(normalizedDisplayValue)) return;
                  setRendaInput(prev => ({ ...prev, single: normalizedDisplayValue }));

                  if (normalizedDisplayValue === '' || normalizedDisplayValue === ',' || normalizedDisplayValue.endsWith(',')) {
                    if (normalizedDisplayValue === '') {
                      handleValueChange('');
                    }
                    return;
                  }

                  const numValue = parseFloat(normalizedDisplayValue.replace(',', '.'));
                  if (!isNaN(numValue)) {
                    handleValueChange(numValue);
                  }
                  return;
                }

                if (value === '') {
                  handleValueChange('');
                  return;
                }

                const numValue = parseFloat(value);
                if (isScoreFc && numValue > 1000) {
                  // Não atualizar se for maior que 1000
                  return;
                }
                if (!isNaN(numValue)) {
                  handleValueChange(numValue);
                }
              }}
              style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '150px' }}
            />
            {isScoreFc && (
              <span style={{ fontSize: '12px', color: '#0066cc', fontWeight: '500', whiteSpace: 'nowrap' }}>
                Máximo: 1000
              </span>
            )}
            {isRendaField && (
              <span style={{ fontSize: '12px', color: '#0066cc', fontWeight: '500', whiteSpace: 'nowrap' }}>
                x Aluguel + Encargos
              </span>
            )}
          </div>
        );

      case 'select':
        return (
          <select
            value={condition.value || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' }}
          >
            <option value="">Selecione...</option>
            {options && options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'multi_select':
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', minWidth: '200px' }}>
            {options && options.map(opt => (
              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                  type="checkbox"
                  checked={(condition.value || []).includes(opt.value)}
                  onChange={(e) => {
                    const currentValue = condition.value || [];
                    const newValue = e.target.checked
                      ? [...currentValue, opt.value]
                      : currentValue.filter(v => v !== opt.value);
                    handleValueChange(newValue);
                  }}
                />
                <span style={{ fontSize: '14px' }}>{opt.label}</span>
              </label>
            ))}
          </div>
        );

      case 'compound_boolean': {
        const currentCompoundValues = Array.isArray(condition.value)
          ? condition.value
          : typeof condition.value === 'boolean'
            ? [condition.value]
            : [];

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', minWidth: '150px' }}>
            {options && options.map(opt => (
              <label key={String(opt.value)} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                  type="checkbox"
                  checked={currentCompoundValues.includes(opt.value)}
                  onChange={(e) => {
                    const newValue = e.target.checked
                      ? [...currentCompoundValues, opt.value]
                      : currentCompoundValues.filter(v => v !== opt.value);

                    onChange(index, {
                      ...condition,
                      value: newValue,
                      sub_conditions: newValue.includes(true)
                        ? condition.sub_conditions
                        : undefined
                    });
                  }}
                />
                <span style={{ fontSize: '14px' }}>{opt.label}</span>
              </label>
            ))}
          </div>
        );
      }

      default:
        return (
          <input
            type="text"
            value={condition.value || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' }}
          />
        );
    }
  };

  const renderSubFields = () => {
    if (!showSubFields || !selectedField.sub_fields) return null;

    const isNegativacao = selectedField.id === 'negativacao';
    const hasValorCriterionEnabled = isNegativacao && selectedField.sub_fields.some(subField => {
      if (!isNegativacaoOptionalField(subField.id)) return false;
      const subCondition = condition.sub_conditions && condition.sub_conditions[subField.id]
        ? condition.sub_conditions[subField.id]
        : {};
      return isOptionalSubFieldEnabled(subField.id, subCondition);
    });

    return (
      <div style={{ 
        marginLeft: '40px', 
        marginTop: '12px', 
        padding: '16px', 
        backgroundColor: '#f9f9f9', 
        borderLeft: '3px solid #0066cc',
        borderRadius: '4px'
      }}>
        <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#666' }}>
          {isNegativacao
            ? 'Critérios Adicionais (quantidade obrigatória; marque pelo menos um critério de valor):'
            : 'Critérios Adicionais (obrigatórios):'}
        </div>
        {isNegativacao && showValidation && validationErrors.missingValorCriterion && (
          <div style={{ marginBottom: '8px', fontSize: '12px', color: '#dc3545', fontWeight: '500' }}>
            Marque Valor Máx (R$) e/ou Percentual sobre renda (%).
          </div>
        )}
        {selectedField.sub_fields.map(subField => {
          const defaultOperator = subField.operators && subField.operators[0] ? subField.operators[0].value : '';
          const subCondition = condition.sub_conditions && condition.sub_conditions[subField.id] 
            ? condition.sub_conditions[subField.id] 
            : { operator: defaultOperator, value: '' };
          const isOptional = isNegativacaoOptionalField(subField.id);
          const isEnabled = isOptionalSubFieldEnabled(subField.id, subCondition);
          const isSubFieldEmpty = isEnabled && (
            !subCondition.operator ||
            subCondition.value === '' ||
            subCondition.value === null ||
            subCondition.value === undefined
          );
          const showSubFieldError = showValidation && Boolean(validationErrors.subFields[subField.id]);
          const borderColor = showSubFieldError ? ERROR_BORDER : '#ddd';
          const backgroundColor = showSubFieldError ? ERROR_BACKGROUND : '#fff';
          
          return (
            <div key={subField.id} style={{ 
              display: 'flex', 
              gap: '12px', 
              alignItems: 'center', 
              marginBottom: '8px',
              padding: '8px',
              backgroundColor: backgroundColor,
              border: `1px solid ${borderColor}`,
              borderRadius: '4px',
              flexWrap: 'wrap'
            }}>
              {isOptional && (
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={(e) => handleSubFieldEnabledChange(subField.id, e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
              )}
              <label style={{ minWidth: '140px', fontSize: '14px', color: isEnabled ? '#444' : '#999' }}>
                {subField.label}:
                {!isOptional && (
                  <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span>
                )}
              </label>

              {isEnabled && (
                <>
              <select
                value={subCondition.operator || ''}
                onChange={(e) => handleSubConditionChange(subField.id, e.target.value, subCondition.value)}
                style={{ 
                  padding: '6px', 
                  border: fieldBorder(showSubFieldError), 
                  borderRadius: '4px', 
                  fontSize: '14px',
                  backgroundColor: '#fff'
                }}
              >
                <option value="">Selecione...</option>
                {subField.operators.map(op => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>

              <input
                type={selectedField.id === 'negativacao' && subField.id === 'valor' ? 'text' : 'number'}
                step={subField.step || '1'}
                max={
                  subField.id === 'valor_percentual' ? '100' :
                  (subField.id === 'quantidade' &&
                    (selectedField.id === 'negativacao' || selectedField.id.startsWith('acoes_'))) ? '10000' :
                  (selectedField.id.startsWith('acoes_') && subField.id === 'tempo') ? '600' :
                  undefined
                }
                value={selectedField.id === 'negativacao' && subField.id === 'valor' 
                  ? negativeValorInput 
                  : (subCondition.value || '')}
                onChange={(e) => {
                  let value = e.target.value;
                  
                  if (selectedField.id === 'negativacao' && subField.id === 'valor') {
                    const formatted = formatMoneyDisplay(value);
                    setNegativeValorInput(formatted);
                    
                    const numValue = parseMoneyValue(formatted);
                    if (numValue === '' || !isNaN(numValue)) {
                      handleSubConditionChange(subField.id, subCondition.operator, numValue);
                    }
                    return;
                  }
                  
                  if ((selectedField.id === 'negativacao' || selectedField.id.startsWith('acoes_')) && subField.id === 'quantidade') {
                    const numValue = parseFloat(value) || '';
                    if (numValue !== '' && numValue > 10000) {
                      return;
                    }
                    handleSubConditionChange(subField.id, subCondition.operator, numValue);
                    return;
                  }
                  
                  if (selectedField.id.startsWith('acoes_') && subField.id === 'tempo') {
                    const numValue = parseFloat(value) || '';
                    if (numValue !== '' && numValue > 600) {
                      return;
                    }
                    handleSubConditionChange(subField.id, subCondition.operator, numValue);
                    return;
                  }

                  if (subField.id === 'valor_percentual') {
                    const numValue = parseFloat(value) || '';
                    if (numValue !== '' && numValue > 100) {
                      return;
                    }
                    handleSubConditionChange(subField.id, subCondition.operator, numValue);
                    return;
                  }
                  
                  const numValue = parseFloat(value) || '';
                  handleSubConditionChange(subField.id, subCondition.operator, numValue);
                }}
                placeholder={
                  selectedField.id === 'negativacao' && subField.id === 'valor'
                    ? 'Ex: 1000000'
                    : subField.id === 'valor_percentual'
                      ? 'Ex: 30'
                    : subField.id === 'quantidade'
                      ? 'Digite a quantidade'
                      : subField.id === 'tempo'
                        ? 'Digite o tempo'
                        : 'Digite o valor'
                }
                style={{ 
                  padding: '6px', 
                  border: fieldBorder(showSubFieldError), 
                  borderRadius: '4px', 
                  width: selectedField.id === 'negativacao' && subField.id === 'valor' ? '150px' : '120px', 
                  fontSize: '14px',
                  backgroundColor: '#fff'
                }}
              />
              {(selectedField.id === 'negativacao' || selectedField.id.startsWith('acoes_')) && subField.id === 'quantidade' && (
                <span style={{ fontSize: '12px', color: '#0066cc', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  Máximo: 10.000
                </span>
              )}
              {selectedField.id.startsWith('acoes_') && subField.id === 'tempo' && (
                <span style={{ fontSize: '12px', color: '#0066cc', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  Máximo: 600
                </span>
              )}
              {selectedField.id === 'negativacao' && subField.id === 'valor' && (
                <span style={{ fontSize: '12px', color: '#0066cc', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  Máximo: R$ 1.000.000
                </span>
              )}
              {subField.id === 'valor_percentual' && (
                <span style={{ fontSize: '12px', color: '#0066cc', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  Máximo: 100%
                </span>
              )}
              {showSubFieldError && (
                <span style={{ color: '#dc3545', fontSize: '12px', fontWeight: '500' }}>
                  (obrigatório)
                </span>
              )}
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const handleOrigemRendaSomaEnabledChange = (enabled) => {
    const somaSubField = selectedField && selectedField.sub_fields
      ? selectedField.sub_fields.find(f => f.id === 'soma_renda')
      : null;
    const defaultOperator = somaSubField && somaSubField.operators && somaSubField.operators[0]
      ? somaSubField.operators[0].value
      : 'greater_than';
    const current = (condition.sub_conditions && condition.sub_conditions.soma_renda) || {
      operator: defaultOperator,
      value: ''
    };

    handleSubConditionChange(
      'soma_renda',
      current.operator || defaultOperator,
      current.value !== undefined && current.value !== null ? current.value : '',
      { enabled }
    );
  };

  const renderOrigemRendaSomaBlock = () => {
    if (!showOrigemRendaSoma || !selectedField || !selectedField.sub_fields) return null;

    const somaSubField = selectedField.sub_fields.find(f => f.id === 'soma_renda');
    if (!somaSubField) return null;

    const defaultOperator = somaSubField.operators && somaSubField.operators[0]
      ? somaSubField.operators[0].value
      : 'greater_than';
    const subCondition = (condition.sub_conditions && condition.sub_conditions.soma_renda) || {
      operator: defaultOperator,
      value: ''
    };
    const isEnabled = subCondition.enabled === true;
    const isSomaBetween = subCondition.operator === 'between';
    const showSomaError = showValidation && validationErrors.subFields && validationErrors.subFields.soma_renda;

    return (
      <div style={{
        marginLeft: '40px',
        marginTop: '12px',
        padding: '16px',
        backgroundColor: showSomaError ? ERROR_BACKGROUND : '#f9f9f9',
        borderLeft: `3px solid ${showSomaError ? ERROR_BORDER : '#0066cc'}`,
        borderRadius: '4px'
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: isEnabled ? '12px' : 0 }}>
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={(e) => handleOrigemRendaSomaEnabledChange(e.target.checked)}
          />
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#666' }}>
            {somaSubField.label}
          </span>
        </label>

        {isEnabled && (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={subCondition.operator || defaultOperator}
              onChange={(e) => {
                const newOperator = e.target.value;
                const newValue = newOperator === 'between' ? { min: '', max: '' } : '';
                handleSubConditionChange('soma_renda', newOperator, newValue, { enabled: true });
              }}
              style={{
                padding: '6px',
                border: fieldBorder(showSomaError),
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            >
              {somaSubField.operators.map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>

            {isSomaBetween ? (
              <>
                <span style={{ fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}>DE:</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={origemRendaSomaInput.min}
                  onChange={(e) => {
                    handleMultiplierInputChange('min', e.target.value, (numValue) => {
                      handleSubConditionChange(
                        'soma_renda',
                        subCondition.operator,
                        {
                          ...(subCondition.value || {}),
                          min: numValue === '' ? '' : numValue
                        },
                        { enabled: true }
                      );
                    });
                  }}
                  placeholder="0"
                  style={{ width: '100px', padding: '6px', border: fieldBorder(showSomaError), borderRadius: '4px' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}>À:</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={origemRendaSomaInput.max}
                  onChange={(e) => {
                    handleMultiplierInputChange('max', e.target.value, (numValue) => {
                      handleSubConditionChange(
                        'soma_renda',
                        subCondition.operator,
                        {
                          ...(subCondition.value || {}),
                          max: numValue === '' ? '' : numValue
                        },
                        { enabled: true }
                      );
                    });
                  }}
                  placeholder="0"
                  style={{ width: '100px', padding: '6px', border: fieldBorder(showSomaError), borderRadius: '4px' }}
                />
              </>
            ) : (
              <input
                type="text"
                inputMode="decimal"
                value={origemRendaSomaInput.single}
                onChange={(e) => {
                  handleMultiplierInputChange('single', e.target.value, (numValue) => {
                    handleSubConditionChange(
                      'soma_renda',
                      subCondition.operator,
                      numValue === '' ? '' : numValue,
                      { enabled: true }
                    );
                  });
                }}
                placeholder="0"
                style={{ width: '100px', padding: '6px', border: fieldBorder(showSomaError), borderRadius: '4px' }}
              />
            )}

            <span style={{ fontSize: '12px', color: '#0066cc', fontWeight: '500', whiteSpace: 'nowrap' }}>
              x Aluguel + Encargos (soma das origens selecionadas)
            </span>

            {showSomaError && (
              <span style={{ color: '#dc3545', fontSize: '12px', fontWeight: '500' }}>
                (obrigatório)
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const rowHasError = showValidation && !isValid;
  const fieldBorder = (hasError) => `1px solid ${hasError ? ERROR_BORDER : '#ddd'}`;

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        alignItems: 'flex-start', 
        padding: '16px', 
        backgroundColor: rowHasError ? ERROR_BACKGROUND : '#fff',
        border: `1px solid ${rowHasError ? ERROR_BORDER : '#e0e0e0'}`,
        borderRadius: '6px',
        marginBottom: '12px'
      }}>
        <div style={{ display: 'flex', gap: '12px', flex: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={condition.field || ''}
            onChange={handleFieldChange}
            style={{
              padding: '8px',
              border: fieldBorder(showValidation && validationErrors.field),
              borderRadius: '4px',
              minWidth: '200px',
              fontSize: '14px',
              backgroundColor: showValidation && validationErrors.field ? ERROR_BACKGROUND : '#fff'
            }}
          >
            <option value="">Selecione o Campo</option>
            {availableFields.map(field => (
              <option key={field.id} value={field.id}>{field.label}</option>
            ))}
            {condition.field && !availableFields.find(f => f.id === condition.field) && (
              <option key={condition.field} value={condition.field}>
                {(metadata.fields.find(f => f.id === condition.field) || {}).label}
              </option>
            )}
          </select>

          {selectedField && (
            <>
              <select
                value={condition.operator || ''}
                onChange={handleOperatorChange}
                style={{
                  padding: '8px',
                  border: fieldBorder(showValidation && validationErrors.operator),
                  borderRadius: '4px',
                  minWidth: '160px',
                  fontSize: '14px',
                  backgroundColor: showValidation && validationErrors.operator ? ERROR_BACKGROUND : '#fff'
                }}
              >
                {operators.map(op => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>

              {renderValueInput()}
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => onRemove(index)}
          style={{
            width: '32px',
            height: '32px',
            padding: '0',
            backgroundColor: '#fff',
            color: '#999',
            border: '1px solid #ddd',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontFamily: 'monospace',
            flexShrink: 0
          }}
          title="Remover"
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#f5f5f5';
            e.target.style.color = '#666';
            e.target.style.borderColor = '#999';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#fff';
            e.target.style.color = '#999';
            e.target.style.borderColor = '#ddd';
          }}
        >
          ×
        </button>
      </div>

      {renderSubFields()}
      {renderOrigemRendaSomaBlock()}
    </div>
  );
};

export default ConditionRow;
